import {
  Anchor,
  ILinkProps,
  Link,
} from "@faire/browser-routing/components/LinkAnchor";
import { isExternalOrHashLink } from "@faire/browser-routing/core/urlUtils";
import { createPath, LocationDescriptor } from "history";
import { HTMLAttributeAnchorTarget } from "react";

import { sanitizeLink } from "./sanitizeLink";

type AnchorAttributes = {
  as: typeof Anchor;
  linkProps: { href: string };
};

type LinkAttributes = {
  as: typeof Link;
  linkProps: {
    to: ILinkProps["to"];
    replace: ILinkProps["replace"];
  };
};

export type PolimorphicLinkAttributes = AnchorAttributes | LinkAttributes;

// Adapted from core/web/src/slate-deprecated/Button/utils
/**
 * Returns the appropriate attributes for the link based on the to and other anchor props.
 *
 * If the link has `linkTo` be external, this hook returns `as={Anchor}` with the href set.
 *
 * Otherwise, this hook returns `as={Link}` with the `linkTo` and `replace` set.
 *
 * Callback-form `linkTo` is intentionally unsupported: resolving it required a live
 * react-router history entry, and there are no known production callers.
 *
 * @param props - The polimorphic link props.
 * @returns The appropriate attributes for the link component.
 */
export const usePolimorphicLinkAttributes = <
  T extends {
    target?: HTMLAttributeAnchorTarget;
    replace?: boolean;
    linkTo: LocationDescriptor<unknown>;
  },
>({
  linkTo,
  ...props
}: T): PolimorphicLinkAttributes => {
  return resolvePolimorphicLinkAttributes({
    ...props,
    linkTo,
  });
};

// Adapted from core/web/src/slate-deprecated/Button/utils
/**
 * Returns the appropriate attributes for the link based on the to and other anchor props.
 *
 * If the `linkTo` is undefined, this hook returns undefined.
 *
 * If the link has `linkTo` be external, this hook returns `as={Anchor}` with the href set.
 *
 * Otherwise, this hook returns `as={Link}` with the `linkTo` and `replace` set.
 *
 * Callback-form `linkTo` is intentionally unsupported: resolving it required a live
 * react-router history entry, and there are no known production callers.
 *
 * @param props - The potentially polimorphic link props (depends on `linkTo` prop).
 * @returns The appropriate attributes for the link component (undefined if `linkTo` prop is not provided).
 */
export const useMaybePolimorphicLinkAttributes = <
  T extends {
    target?: HTMLAttributeAnchorTarget;
    replace?: boolean;
    linkTo: LocationDescriptor<unknown> | undefined;
  },
>({
  linkTo,
  ...props
}: T): PolimorphicLinkAttributes | undefined => {
  if (linkTo === undefined) {
    return;
  }

  return resolvePolimorphicLinkAttributes({
    ...props,
    linkTo,
  });
};

const resolvePolimorphicLinkAttributes = <
  T extends {
    target?: HTMLAttributeAnchorTarget;
    replace?: boolean;
    linkTo: LocationDescriptor<unknown>;
  },
>({
  linkTo,
  replace,
  target,
  ...rest
}: T): PolimorphicLinkAttributes => {
  if ("download" in rest || isExternalOrHashLink(linkTo)) {
    const resolved =
      typeof linkTo === "string"
        ? sanitizeLink(linkTo, target)
        : createPath(linkTo);

    return {
      as: Anchor,
      linkProps: { href: resolved },
    };
  }

  return {
    as: Link,
    linkProps: {
      to: sanitizeLink(linkTo, target) as LocationDescriptor<unknown>,
      replace,
    },
  };
};
